import { useCallback } from 'react';
import { computeMatchScrollTop } from '../pdfScroll';
import type { PdfViewerState } from './usePdfViewerState';

export function usePdfScroll(state: PdfViewerState) {
  const { containerRef, pageContainerRefs, scale } = state;

  const scrollToPage = useCallback((
    pageNum: number,
    behavior: ScrollBehavior = 'auto',
    block: 'start' | 'center' = 'start',
  ) => {
    const container = containerRef.current;
    const pageContainer = pageContainerRefs.current.get(pageNum);
    if (!container || !pageContainer) return;

    let top = pageContainer.offsetTop;
    if (block === 'center') {
      top = pageContainer.offsetTop - Math.max(
        0,
        (container.clientHeight - pageContainer.offsetHeight) / 2,
      );
    }

    const maxScrollTop = container.scrollHeight - container.clientHeight;
    const clampedTop = maxScrollTop > 0
      ? Math.max(0, Math.min(top, maxScrollTop))
      : Math.max(0, top);

    if (typeof container.scrollTo === 'function') {
      container.scrollTo({ top: clampedTop, behavior });
    } else {
      container.scrollTop = clampedTop;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- refs are stable
  }, []);

  /**
   * Brings a search match into view. scrollToPage only ever reached the top of
   * the page, so a match halfway down a tall page left the reader hunting for
   * the highlight — the match's own offset was computed when painting the
   * rectangle and then discarded.
   */
  const scrollToMatch = useCallback((
    pageNum: number,
    pdfRect?: [number, number, number, number],
    behavior: ScrollBehavior = 'auto',
  ) => {
    const container = containerRef.current;
    const pageContainer = pageContainerRefs.current.get(pageNum);
    if (!container || !pageContainer) return;

    let matchTop: number | undefined;
    let matchHeight = 0;
    if (pdfRect) {
      // The rect is in PDF space (origin bottom-left); the layer is in view space.
      const pageHeight = pageContainer.offsetHeight;
      const top = Math.min(pdfRect[1], pdfRect[3]);
      const bottom = Math.max(pdfRect[1], pdfRect[3]);
      matchHeight = (bottom - top) * scale;
      matchTop = Math.max(0, pageHeight - bottom * scale);
    }

    const top = computeMatchScrollTop({
      pageOffsetTop: pageContainer.offsetTop,
      matchTop,
      matchHeight,
      containerHeight: container.clientHeight,
      scrollHeight: container.scrollHeight,
    });

    if (typeof container.scrollTo === 'function') container.scrollTo({ top, behavior });
    else container.scrollTop = top;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- refs are stable
  }, [scale]);

  return { scrollToPage, scrollToMatch };
}
